import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.js';
import AppShell from '@/layouts/AppShell.jsx';
import PlatformLoginPage from '@/platform/PlatformLoginPage.jsx';
import PlatformShell from '@/platform/PlatformShell.jsx';
import CustomersPage from '@/platform/CustomersPage.jsx';
import CustomerDetailPage from '@/platform/CustomerDetailPage.jsx';
import ProfilesPage from '@/platform/ProfilesPage.jsx';
import SecurityPage from '@/platform/SecurityPage.jsx';
import { usePlatformAuth } from '@/platform/platformAuth.js';
import { profileExtensions } from '@/config/profileExtensions.js';

// LoginPage is the ONLY eager page route. It is the first paint for a signed-out
// visitor, so making it lazy would add a network round trip before anything renders.
// Everything else is reachable only after `App` (and therefore this chunk) has already
// loaded, so splitting them costs nothing on the critical path.
//
// DashboardPage is lazy even though it is the post-login landing page: it is the sole
// importer of recharts, and leaving it eager dragged the whole charts vendor chunk into
// the login payload. See docs/PHASE-1-NOTES.md.
import LoginPage from '@/pages/auth/LoginPage.jsx';

const isPlatformHost = typeof window !== 'undefined' && window.location.hostname === 'admin.onlinepayroll.top';

const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage.jsx'));
const EmployeesPage = lazy(() => import('@/pages/employees/EmployeesPage.jsx'));
const EmployeeFormPage = lazy(() => import('@/pages/employees/EmployeeFormPage.jsx'));
const EmployeeProfilePage = lazy(() => import('@/pages/employees/EmployeeProfilePage.jsx'));
const AttendancePage = lazy(() => import('@/pages/attendance/AttendancePage.jsx'));
const LeavePage = lazy(() => import('@/pages/leave/LeavePage.jsx'));
const OvertimePage = lazy(() => import('@/pages/overtime/OvertimePage.jsx'));
const ShortLeavePage = lazy(() => import('@/pages/short-leave/ShortLeavePage.jsx'));
const LoansPage = lazy(() => import('@/pages/loans/LoansPage.jsx'));
const AdvancesPage = lazy(() => import('@/pages/advances/AdvancesPage.jsx'));
const SalaryHoldsPage = lazy(() => import('@/pages/salary-holds/SalaryHoldsPage.jsx'));
const PayrollRunsPage = lazy(() => import('@/pages/payroll/PayrollRunsPage.jsx'));
const PayrollRunDetailPage = lazy(() => import('@/pages/payroll/PayrollRunDetailPage.jsx'));
const AllowancesMonthPage = lazy(() => import('@/pages/payroll/AllowancesMonthPage.jsx'));
const DeductionsMonthPage = lazy(() => import('@/pages/payroll/DeductionsMonthPage.jsx'));
const MidMonthPaymentsPage = lazy(() => import('@/pages/payroll/MidMonthPaymentsPage.jsx'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage.jsx'));
const AuditLogPage = lazy(() => import('@/pages/audit/AuditLogPage.jsx'));
const DepartmentsPage = lazy(() => import('@/pages/settings/DepartmentsPage.jsx'));
const DesignationsPage = lazy(() => import('@/pages/settings/DesignationsPage.jsx'));
const PayGradesPage = lazy(() => import('@/pages/settings/PayGradesPage.jsx'));
const HolidaysPage = lazy(() => import('@/pages/settings/HolidaysPage.jsx'));
const LeaveTypesPage = lazy(() => import('@/pages/settings/LeaveTypesPage.jsx'));
const SalaryComponentsPage = lazy(() => import('@/pages/settings/SalaryComponentsPage.jsx'));
const CompanySettingsPage = lazy(() => import('@/pages/settings/CompanySettingsPage.jsx'));
const TaxTablesPage = lazy(() => import('@/pages/settings/TaxTablesPage.jsx'));
const UsersPage = lazy(() => import('@/pages/settings/UsersPage.jsx'));
const MasterDetailsHub = lazy(() => import('@/pages/settings/MasterDetailsHub.jsx'));
const HRPoliciesHub = lazy(() => import('@/pages/settings/HRPoliciesHub.jsx'));
const PreferenceHub = lazy(() => import('@/pages/settings/PreferenceHub.jsx'));
const SampleExtensionPage = profileExtensions.samplePage
  ? lazy(() => import('@/extensions/sample-page/SamplePage.jsx'))
  : null;

function PrivateRoute({ children }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function PlatformPrivateRoute({ children }) {
  const authenticated = usePlatformAuth((s) => s.authenticated);
  if (!authenticated) return <Navigate to="/platform/login" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={isPlatformHost ? <PlatformLoginPage /> : <LoginPage />} />
      <Route path="/platform/login" element={<PlatformLoginPage />} />
      <Route
        path="/platform"
        element={
          <PlatformPrivateRoute>
            <PlatformShell />
          </PlatformPrivateRoute>
        }
      >
        <Route index element={<Navigate to="/platform/customers" replace />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="profiles" element={<ProfilesPage />} />
        <Route path="security" element={<SecurityPage />} />
      </Route>
      <Route
        path="/"
        element={
          isPlatformHost ? <Navigate to="/platform/login" replace /> : (
            <PrivateRoute>
              <AppShell />
            </PrivateRoute>
          )
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />

        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/new" element={<EmployeeFormPage />} />
        <Route path="employees/:id/edit" element={<EmployeeFormPage />} />
        <Route path="employees/:id" element={<EmployeeProfilePage />} />

        <Route path="attendance" element={<AttendancePage />} />
        <Route path="leave" element={<LeavePage />} />
        <Route path="overtime" element={<OvertimePage />} />
        <Route path="short-leave" element={<ShortLeavePage />} />

        <Route path="loans" element={<LoansPage />} />
        <Route path="advances" element={<AdvancesPage />} />
        <Route path="salary-holds" element={<SalaryHoldsPage />} />

        <Route path="payroll" element={<PayrollRunsPage />} />
        <Route path="payroll/:id" element={<PayrollRunDetailPage />} />
        <Route path="payroll/allowances" element={<AllowancesMonthPage />} />
        <Route path="payroll/deductions" element={<DeductionsMonthPage />} />
        <Route path="payroll/mid-month" element={<MidMonthPaymentsPage />} />

        <Route path="reports" element={<ReportsPage />} />
        <Route path="audit" element={<AuditLogPage />} />

        {/* Hub routes — new 3-item Settings sidebar */}
        <Route path="settings/master" element={<MasterDetailsHub />} />
        <Route path="settings/hr-policies" element={<HRPoliciesHub />} />
        <Route path="settings/preference" element={<PreferenceHub />} />

        {/* Legacy individual settings routes (kept for direct linking) */}
        <Route path="settings/company" element={<CompanySettingsPage />} />
        <Route path="settings/users" element={<UsersPage />} />
        <Route path="settings/departments" element={<DepartmentsPage />} />
        <Route path="settings/designations" element={<DesignationsPage />} />
        <Route path="settings/pay-grades" element={<PayGradesPage />} />
        <Route path="settings/holidays" element={<HolidaysPage />} />
        <Route path="settings/leave-types" element={<LeaveTypesPage />} />
        <Route path="settings/salary-components" element={<SalaryComponentsPage />} />
        <Route path="settings/tax-tables" element={<TaxTablesPage />} />
        {SampleExtensionPage && <Route path="sample-extension" element={<SampleExtensionPage />} />}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
